import { DataAction, Payload, Persistence, StateRepository } from '@angular-ru/ngxs/decorators';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import { EventEmitter, Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from '@angular/router';
import { Action, Selector, SelectorOptions, State, StateContext, Store } from '@ngxs/store';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import produce from 'immer';
import * as moment from 'moment';
import { combineLatest, firstValueFrom, forkJoin, from, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { catchError, filter, first, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { IObject } from 'src/app/models/+global';
import { AddressTypeIds, AddressTypeIdsEnum, MemberAddress } from 'src/app/models/address';
import { LabelValueLower } from 'src/app/models/keyvalue';
import { ICustomerService, IMemberContextTaskOrchestrator, Member, MemberContext, MemberNotification, MemberRequestItem, MemberSSNRequestItem, MMRRecord, PersonRelations, SessionStatus } from 'src/app/models/member';
import { MemberPhone } from 'src/app/models/phone';
import { Product } from 'src/app/models/product';
import { IMemberRelationship } from 'src/app/models/relationship';
import { SignalREvent } from 'src/app/models/signalr';
import { Directions, TelephonyContext, TelephonyContextRequest, TelephonyContextResponse, TelephonyMemberItem } from 'src/app/models/telephonyv2';
import { Tenant } from 'src/app/models/tenant';
import {
    IMemberSearchMetadataRequest,
    IMemberSearchMetadataResponse,
    IMemberSearchRequest,
    IMemberSearchResponse
} from 'src/app/pages/_searchs/member/member-search.model';
import { AppInsightsService } from 'src/app/services/app-insights/appinsights.service';
import { KeyedMutex } from 'src/app/shared/Mutex';
import { NavigationHandlerService } from 'src/app/shared/navigation-handler/navigation-handler.service';
import { INavigationSubscriber } from 'src/app/shared/navigation-handler/navigation-handler.state.models';
import { AlertService, MessageSeverity } from '../alert.service';
import { ConfigurationService } from '../configuration/configuration.service';
import { KvpManagerService } from '../kvp-mamager/kvp-manager.service';
import { PhoneService } from '../phone/phone.service';
import { ProductService } from '../product/product.service';
import { SignalRService } from '../signalR/signalr.service';
import { ICommand, ICommandHost } from '../task/command/command.models';
import { ITaskCommandWrapper, ITaskHost, ITaskWrapper } from '../task/task.state.models';
import { TelephonyQueryParamsService } from '../telephony/telephony-queryparams.service';
import { TenantService } from '../tenant/tenant.service';
import { RoleService } from '../user/role.service';
import { UserService } from '../user/user.service';
import { Utilities } from '../utilities';
import { TelephonyFormattedQueryParams } from './../telephony/telephony-queryparams.state.models';
import { MemberEndpoint } from './member-endpoint.service';
import { MemberSessionService } from './member-session.service';
import { MemberTaskService } from './member-task.service';
import { ClearMemberSessionAction, IMemberCallManager, IMemberStateModel, ISurveyInfoViewModel } from './member.models';
import { BlazorInteropService } from 'src/app/components/blazor-shell/blazor-interop.service';


@Persistence([
    {
        path: 'memberState.sessionsTracker',
        existingEngine: localStorage,
    },
    {
        path: 'memberState.currentSession',
        existingEngine: sessionStorage
    }
])
@StateRepository()
@State<IMemberStateModel>({
    name: 'memberState',
    defaults: {
        currentSession: {
            initialized: false,
            base64: null,
            legacyBase64: null,
            tenantId: null,
            memberContext: null,
            mmr: null,
            trackingCommands: []
        },
        sessionsTracker: {

        }

    }
})
@Injectable()
export class MemberService extends NgxsDataRepository<IMemberStateModel> implements IMemberCallManager, ITaskHost, ICommandHost, Resolve<Member> {
    private readonly _byMemberMutex = new KeyedMutex<string>();
    private static readonly memberContextMap: Map<string, MemberContext> = new Map<string, MemberContext>();

    previousTenantIdSync: string;
    tenantChanged$$: Subscription;
    tenantChanged$ = new EventEmitter<string>();

    private ssnViewEventSubject = new Subject<boolean>();


    signalR$ = new EventEmitter<SignalREvent>();
    signalRConnection: any;

    tenant$ = combineLatest([this.store.select<Tenant[]>(TenantService.activeTenants), this.base64$])
        .pipe(filter(([globalTenants, member]) => !!globalTenants), map(([globalTenants, member]) => {
            return globalTenants.find(t => t.tenantName === this.tenantIdSync);
        }));

        get tenant(){
            return this.store.selectSnapshot<Tenant[]>(TenantService.activeTenants)?.find(t => t.tenantName === this.tenantIdSync);
        }

    confirmCloseMemberSessionWithPendings: boolean;
    confirmCloseMemberSessionWithPendings$ = new EventEmitter<boolean>(null);
    showMemberHistory$ = new EventEmitter<boolean>();

    @SelectorOptions({ injectContainerState: false })
    @Selector([MemberService])
    static isolatedMemberContext(state: IMemberStateModel) {
        return state.currentSession.memberContext;
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([MemberService.isolatedMemberContext])
    static memberContext(entryPointMemberContext: MemberContext) {
        return new MemberContext(entryPointMemberContext);
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([MemberService.memberContext])
    static member(memberContext: MemberContext) {
        return memberContext.member;
    }


    @Selector()
    static base64(state: IMemberStateModel) {
        return state.currentSession.base64;
    }

    @Selector()
    static legacyBase64(state: IMemberStateModel) {
        return state.currentSession.legacyBase64;
    }

    @Selector()
    static memberId(state: IMemberStateModel) {
        return state.currentSession.memberId;
    }

    @Selector()
    static tenantId(state: IMemberStateModel) {
        return state.currentSession.tenantId;
    }

    @Selector()
    static prevoiusTenantId(state: IMemberStateModel) {
        return state.currentSession.previousTenantId;
    }

    @Selector()
    static initialized(state: IMemberStateModel) {
        return state.currentSession.initialized;
    }

    @Selector()
    static mmr(state: IMemberStateModel) {
        return state.currentSession.mmr;
    }

    @Selector()
    static products(state: IMemberStateModel) {
        return state.currentSession.products;
    }

    @Selector()
    static addresses(state: IMemberStateModel) {
        return state.currentSession.addresses;
    }

    @Selector()
    static phones(state: IMemberStateModel) {
        return state.currentSession.phones;
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([MemberService.memberContext])
    static commandsSelector(memberContext: MemberContext) {
        return memberContext.commands.map(_ => _ as ITaskWrapper & ICommand);
    }



    @Selector()
    static onCallSelector(state: IMemberStateModel) {
        return state.currentSession.memberContext.metadata.onCall;
    }

    @Selector()
    static verifiedSelector(state: IMemberStateModel) {
        return state.currentSession.memberContext.metadata.verified;
    }


    @SelectorOptions({ injectContainerState: false })
    @Selector([MemberService.tenantId, TenantService.activeTenants])
    static tenant(tenantId: string, globalTenants: Tenant[]) {
        return globalTenants.find(t => t.tenantName === tenantId)
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([MemberService])
    private static isolatedTrackingCommands(state: IMemberStateModel) {
        return Utilities.cloneHard(state.currentSession.trackingCommands);
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([MemberService.isolatedTrackingCommands])
    static trackingCommands(commands: ICommand[]) {
        return commands;
    }

    @Selector()
    static sessionStatusSelector(state: IMemberStateModel) {
        return state.currentSession.memberContext.metadata.sessionStatus;
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([MemberService.memberContext])
    static notificationsSelector(memberContext: MemberContext) {
        return memberContext.notifications;
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([MemberService.member])
    static relations(member: Member) {
        return member.relations;
    }

    @Selector()
    static MMRSelector(state: IMemberStateModel) {
        return state.currentSession.memberContext.mmr;
    }

    @Selector()
    static RelationshipSelector(state: IMemberStateModel) {
        return state.currentSession.memberContext.relationships;
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([MemberService.memberContext])
    static memberTaskOrchestratorConfigSelector(memberContext: MemberContext): IMemberContextTaskOrchestrator {
        return memberContext?.taskOrchestrator;
    }


    @SelectorOptions({ injectContainerState: false })
    @Selector([MemberService.memberContext])
    static customerService(memberContext: MemberContext) {
        return memberContext.customerService;
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([MemberService.customerService])
    static customerServicePhoneNumbers(customerService: ICustomerService) {
        return customerService.phoneNumbers;
    }

    get initialized$(): Observable<boolean> {
        return this.store.select<boolean>(MemberService.initialized);
    }

    get initializedSync(): boolean {
        return this.store.selectSnapshot<boolean>(MemberService.initialized);
    }

    get base64$(): Observable<string> {
        return this.store.select<string>(MemberService.base64);
    }

    get base64Sync(): string {
        return this.store.selectSnapshot<string>(MemberService.base64);
    }

    get memberId$() {
        return this.store.select<string>(MemberService.memberId);
    }

    get memberIdSync() {
        return this.store.selectSnapshot<string>(MemberService.memberId);
    }

    get tenantId$() {
        return this.store.select<string>(MemberService.tenantId);
    }

    get tenantIdSync() {
        return this.store.selectSnapshot<string>(MemberService.tenantId);
    }

    get legacyBase64$() {
        return this.store.select<string>(MemberService.legacyBase64);
    }

    get legacyBase64Sync() {
        return this.store.selectSnapshot<string>(MemberService.legacyBase64);
    }


    get memberContext$(): Observable<MemberContext> {
        return this.store.select<MemberContext>(MemberService.memberContext);
    }

    get memberContextSync(): MemberContext {
        return this.store.selectSnapshot<MemberContext>(MemberService.memberContext);
    }

    get member$(): Observable<Member> {
        return this.store.select<Member>(MemberService.member);
    }

    get memberSync(): Member {
        return this.store.selectSnapshot<Member>(MemberService.member);
    }

    get mmrSync(): MMRRecord {
        return this.store.selectSnapshot<MMRRecord>(MemberService.MMRSelector);
    }

    get mmr$(): Observable<MMRRecord> {
        return this.store.select<MMRRecord>(MemberService.MMRSelector);
    }

    get relationshipSync(): IMemberRelationship[] {
        return this.store.selectSnapshot<IMemberRelationship[]>(MemberService.RelationshipSelector);
    }

    get relationship$(): Observable<IMemberRelationship[]> {
        return this.store.select<IMemberRelationship[]>(MemberService.RelationshipSelector);
    }

    get products$(): Observable<Product[]> {
        return this.store.select<Product[]>(MemberService.products);
    }

    get productsSync(): Product[] {
        return this.store.selectSnapshot<Product[]>(MemberService.products);
    }

    get addresses$(): Observable<MemberAddress[]> {
        return this.store.select<MemberAddress[]>(MemberService.addresses);
    }

    get addressesSync(): MemberAddress[] {
        return this.store.selectSnapshot<MemberAddress[]>(MemberService.addresses);
    }

    get phones$(): Observable<MemberPhone[]> {
        return this.store.select<MemberPhone[]>(MemberService.phones);
    }

    get phonesSync(): MemberPhone[] {
        return this.store.selectSnapshot<MemberPhone[]>(MemberService.phones);
    }

    get commandsSync() {
        return this.store.selectSnapshot<(ICommand & ITaskWrapper)[]>(MemberService.commandsSelector) || [];
    }

    get commands$() {
        return this.store.select<(ICommand & ITaskWrapper)[]>(MemberService.commandsSelector).pipe(map(_ => _ || []));
    }


    get trackingCommandsSync() {
        return this.store.selectSnapshot<ICommand[]>(MemberService.isolatedTrackingCommands) || [];
    }

    get trackingCommands$() {
        return this.store.select<ICommand[]>(MemberService.isolatedTrackingCommands).pipe(map(_ => _ || []));
    }

    get onCallSync() {
        return this.store.selectSnapshot<boolean>(MemberService.onCallSelector);
    }

    get onCall$() {
        return this.store.select<boolean>(MemberService.onCallSelector);
    }

    get sessionStatusSync() {
        return this.store.selectSnapshot<SessionStatus>(MemberService.sessionStatusSelector);
    }

    get sessionStatus$() {
        return this.store.select<SessionStatus>(MemberService.sessionStatusSelector);
    }

    get verifiedSync() {
        return this.store.selectSnapshot<boolean>(MemberService.verifiedSelector);
    }

    get verified$() {
        return this.store.select<boolean>(MemberService.verifiedSelector);
    }


    get commandHostReady$() {
        return this.initialized$;
    }

    get notificationsSync(): MemberNotification[] {
        return this.store.selectSnapshot<MemberNotification[]>(MemberService.notificationsSelector);
    }

    get notifications$(): Observable<MemberNotification[]> {
        return this.store.select<MemberNotification[]>(MemberService.notificationsSelector);
    }

    get relationsSync(): PersonRelations {
        return this.store.selectSnapshot<PersonRelations>(MemberService.relations);
    }

    get relations$(): Observable<PersonRelations> {
        return this.store.select<PersonRelations>(MemberService.relations);
    }

    get memberTaskOrchestratorConfig$() {
        return this.store.select(MemberService.memberTaskOrchestratorConfigSelector);
    }
    get memberTaskOrchestratorConfigSync() {
        return this.store.selectSnapshot(MemberService.memberTaskOrchestratorConfigSelector);
    }
    emitSSNViewGlobalEvent(isViewingSSN: boolean) {
        this.ssnViewEventSubject.next(isViewingSSN);
    }

    getSSNViewGlobalEvent() {
        return this.ssnViewEventSubject.asObservable();
    }

    constructor(
        private store: Store,
        private memberEndpoint: MemberEndpoint,
        private phoneService: PhoneService,
        private productService: ProductService,
        private signalRService: SignalRService,
        public telephonyParamsService: TelephonyQueryParamsService,
        private userService: UserService,
        public taskService: MemberTaskService,
        public tenantService: TenantService,
        private kvpManagerService: KvpManagerService,

        //#region Telephony
        public router: Router,
        public route: ActivatedRoute,
        private alertService: AlertService,
        private configurationService: ConfigurationService,
        private navigationHandlerService: NavigationHandlerService,
        private appInsightsService: AppInsightsService,
        private readonly blazorInteropService: BlazorInteropService,
        //#endregion

    ) {
        super();

        this.navigationHandlerService.addSubscriber({
            id: 'member-navigation',
            isAsync: true,
            navigationCommandFunc: this.handleNavigation.bind(this),
            order: 1,
        });
    }

    static getBase64(tenantId: string, id: string) {
        const urlPattern = '{tenantId}|{memberid}|MemberActor';
        const url = urlPattern.replace('{memberid}', id).replace('{tenantId}', tenantId.toUpperCase());

        return btoa(url);
    }

    static parseBase64(base64: string) {
        let tenantId: string;
        let memberId: string;

        if (base64 != null) {
            let parts: { firstPart: string, secondPart: string };
            parts = Utilities.splitInTwo(atob(base64), '|');
            tenantId = parts.firstPart.toUpperCase();
            memberId = Utilities.splitInTwo(parts.secondPart, '|').firstPart;
        }

        return { tenantId, memberId };
    }

    async ngxsAfterBootstrap() {

        super.ngxsAfterBootstrap();


        // be sure that member is clear(only keep the current member session progress if exists)
        this.clearMember(true);


        // Wait until authenticated to get access to access_token used by interceptor. UserService.userIdentifier need to be replaced by something more specific to access token.
        await combineLatest([this.store.select<string>(UserService.userIdentifier), this.store.select<boolean>(ConfigurationService.initializedSelector)]).pipe(filter(([userIdentifier, configInitialized]) => !!userIdentifier && configInitialized), first()).toPromise();
        // load member when role is changed. After member session id started,
        // skip the first result of currentRole and listen for role changes
        this.userService.currentRole$.pipe(filter(_ => this.initializedSync && !!this.memberSync))
            .subscribe(async _ => {
                await this.refresh();
            });

        this.taskService.setContext(this).activate();

        this.initializeTenantChangeListener();

    }

    initializeTenantChangeListener() {
        this.tenantChanged$$ = this.tenantChanged$.subscribe(tenantId => {
            of(this.getMemberProducts()).pipe(first()).subscribe();
        });
    }

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Member | Observable<Member> | Promise<Member> {
        const base64 = route.params.id;
        return this.initialize(base64);
    }

    initializeSignalR() {
        this.signalRConnection = this.signalRService.initialize({ relativeUrl: 'member-signalr' });
    }

    async refresh() {
        try {
            this.clearMemberCache(this.base64Sync);
            return await firstValueFrom(this.getCurrentMember(true).pipe(first()));
        } catch (error) {
            this.alertService.showStickyMessage(
                'Member Error',
                `Loading Member\r\nError: "${Utilities.getHttpResponseMessages(
                    error
                )}"`,
                MessageSeverity.error,
                error
            );
        }
    }

    public initialize(base64: string, force: boolean = false): Observable<Member> {
        // VUhDfC04OTg0MTcyMzUxMTYyMjkyMzY4fE1lbWJlckFjdG9y
        // ex base64 decoded UHC|-8668556935800333349|MemberActor
        console.log('TASKS: Initializing member => ', base64);
        this.setInitialized(false);
        if (base64) {

            try {
                // clear relative data like cases and mmr. Marking them to been retrieved again
                this.setBase64(base64);
                return this.getCurrentMember().pipe(filter(_ => !!_), first(), tap(async _ => {
                    if (this.base64Sync) {
                        await this.initializeSession();
                        this.setInitialized(true);
                    }
                }), map(_ => _.member));
            } catch (error) {
                console.error(`Error retrieving the member =>`, error);
                return throwError(error);
            }



        } else {
            this.clearMember();
        }

        return of(null);
    }

    getCurrentMemberRequestPayload() {
        const telephonyAwaiter$ = this.telephonyParamsService.telephonyParamsAwaiter$.pipe(filter(_ => !!_));
        const tenants$ = this.tenantService.activeTenants$.pipe(filter(_ => !!_ && _.length > 0));

        return combineLatest([telephonyAwaiter$, tenants$]).pipe(switchMap(([callParams, _]) => {
            const { context } = this.getCurrentTelephonyContext(undefined, this.memberIdSync);
            let params = callParams;
            if (context) {

                params = {
                    contactId: context.contactId,
                    direction: context.direction,
                    directionValue: context.directionId,

                    skillName: context.skillname,

                    metadata: context.metaData,

                    origin: context.origin,
                    originId: context.originId,

                    reason: context.reason,
                    reasonId: context.reasonId
                } as TelephonyFormattedQueryParams;

            }

            const contextInput = {
                ...params,
                directionValue: params.directionValue,
                direction: params.direction ?? 'USER', // some sp expects direction instead sessionDirection
                sessionDirection: params.direction ?? 'USER',
                sessionSkillName: params.skillName,
                tenantId: this.tenantIdSync,
                tenantDisplayName: this.tenantService.findTenantByIdSync(this.tenantIdSync)?.displayName,
                onCall: this.store.selectSnapshot<boolean>(MemberService.onCallSelector),
                userFirstName: this.userService.userSync.firstName,
                userLastName: this.userService.userSync.lastName,
                role: this.store.selectSnapshot<string>(RoleService.currentRoleName),
                user: this.userService.userSync.uniqueName,
                sessionStatus: this.sessionStatusSync,
                initialized: this.initializedSync,
                tasks: (this.trackingCommandsSync || [])
                    .map(_ => _ as ITaskCommandWrapper).filter(_ => _.task)
                    .map(cmd => ({
                        command: cmd.id,
                        task: cmd.task.id,
                        order: cmd.task.executionData.executionOrder,
                        status: cmd.task.executionData.status.value,
                        metadata: cmd.task.executionData.metadata
                    }))
            };

            return of(contextInput);

        }));
    }

    getMemberParametersObject(): IObject {

        let req: IObject = {};

        if (this.memberSync) {

            req.TenantId = this.memberSync.base64TenantId;
            req.MemberId = this.memberSync.id;

            let homeAddress = this.memberSync.addresses.find(a => a.type == AddressTypeIds.Home);
            if (homeAddress) {
                req.homeCity = homeAddress.city;
                req.homeState = homeAddress.state;
                req.homeCounty = homeAddress.county;
                req.homePostalCode = homeAddress.postalCode;
            }
        }
        return req;
    }

    private getCurrentMember(updateCurrent = true): Observable<MemberContext> {

        return this.getCurrentMemberRequestPayload().pipe(switchMap((requestPayload) => {
            if (this.base64Sync) {
                const memberContext$ = this.getMemberHandler(this.base64Sync, requestPayload, false);

                if (updateCurrent) {
                    return memberContext$.pipe(switchMap(memberContext => {
                        if (this.base64Sync) {
                            this.setMemberContext(memberContext);
                        }
                        return this.memberContext$;
                    }));
                } else {
                    return memberContext$;
                }
            } else {
                return of(null);
            }
        }));
    }


    getMember(base64: string, contextInput?: any, instantiate: boolean = true) {
        if (this.base64Sync == base64) {
            console.trace('MemberService.getMember: already initialized');
        }
        return this.getMemberHandler(base64, contextInput, instantiate);

    }

    private getMemberHandler(base64: string, contextInput?: any, instantiate: boolean = true) {

        const params = of({ base64, instantiate });
        const unlock = this._byMemberMutex.lock(base64);

        return forkJoin([params, unlock]).pipe(
            mergeMap(([params, unlock]) => {
                // Retrieve from cache or request to the API
                let result: Observable<{ memberContext?: MemberContext, error?: any }>;
                if (MemberService.memberContextMap.has(params.base64)) {
                    result = of({ memberContext: MemberService.memberContextMap.get(params.base64) });
                } else {
                    result = this.memberEndpoint.getMemberEndpoint(params.base64, contextInput).pipe(map(memberResponse => {
                        if (memberResponse?.member) {
                            memberResponse.member.base64 = base64;
                            const { memberGroupHash, memberGroup } = Member.getMemberHash(memberResponse.member);
                            memberResponse.member.$memberHash = memberGroupHash;
                            memberResponse.member.$memberHashSource = memberGroup;
                        }

                        return instantiate ? new MemberContext(memberResponse) : memberResponse;
                    })).pipe(
                        catchError(error => of({ error })),

                        map(response => (response['error'] ? (response as { error?: any }) : { memberContext: params.instantiate ? new MemberContext(response as MemberContext) : response as MemberContext }))
                    );
                }

                return result.pipe(map(retrieveResponse => ({ ...retrieveResponse, ...params, unlock })));
            }),
            tap(({ memberContext, error, instantiate, base64, unlock }) => {
                // Save the member context into the cache
                if (!MemberService.memberContextMap.has(base64)) {
                    MemberService.memberContextMap.set(base64, memberContext);
                }
                unlock();
            }),
            map(({ memberContext, error }) => {
                // Finally, return the member context or throw the error
                if (error) {
                    throw error;
                }
                return memberContext;
            })
        );

    }

    clearMemberCache(base64: string = null) {
        if (base64) {
            MemberService.memberContextMap.delete(base64);
        } else {
            MemberService.memberContextMap.clear();
        }
    }


    getMemberById(base64: string) {
        return this.memberEndpoint.getMemberById(base64).pipe(map(memberResponse => {
            return memberResponse;
        }));
    }

    getMemberSSN(base64: string) {
        return this.memberEndpoint.getMemberSSN(base64).pipe(map(memberSSNResponse => {
            return memberSSNResponse;
        }));
    }


    getAddressByType(preferredType: AddressTypeIdsEnum, useFallback: boolean = false): Observable<MemberAddress> {
        return this.memberSync.getAddressByType(preferredType, useFallback);
    }

    search(req: IMemberSearchRequest): Observable<IMemberSearchResponse> {
        return this.memberEndpoint.getSearch(req);
    }

    searchMetadata(req: IMemberSearchMetadataRequest): Observable<IMemberSearchMetadataResponse> {
        return this.memberEndpoint.getSearchMetadata(req);
    }

    async tryNextCommand() {
        await this.refresh();
    }

    updateMember(tenantId: any, member: MemberRequestItem): Observable<{ result: boolean }> {
        return this.memberEndpoint
            .updateMember(tenantId, member);
    }
    updateMemberSSN(tenantId: any, member: MemberSSNRequestItem): Observable<boolean> {
        return this.memberEndpoint
            .updateMemberSSN(tenantId, member);
    }

    @DataAction() clearMember(@Payload('keepContextProgress') keepContextProgress?: boolean) {

        const state = Utilities.cloneHard(this.snapshot);
        this.ctx.setState(patch<IMemberStateModel>({ currentSession: this.initialState.currentSession }));

        if (keepContextProgress) {
            this.setTrackingCommands(state.currentSession.trackingCommands as ITaskCommandWrapper[]);
        } else {
            this.setOnCall(false);
            this.clearMemberCache();
        }
    }

    /*Redux State Actions */

    @DataAction()
    async getMemberPhones(@Payload('base64') base64: string) {
        let phones: MemberPhone[] = [];
        if (this.tenantIdSync && this.memberIdSync) {
            phones = await this.phoneService.getPhones(this.tenantIdSync, this.memberIdSync).pipe(first()).toPromise();
        }

        this.ctx.patchState(produce(this.ctx.getState(), (draft: IMemberStateModel) => {
            draft.currentSession.phones = phones;
        }));
    }

    @DataAction()
    async getMemberProducts(): Promise<Product[]> {
        let products: Product[];
        if (this.base64Sync && this.tenantIdSync) {
            products = await this.productService.getProducts(this.tenantIdSync).pipe(first()).toPromise();
        }

        this.ctx.patchState(produce(this.ctx.getState(), (draft: IMemberStateModel) => {
            draft.currentSession.products = products;
        }));

        return products;
    }

    @DataAction()
    setInitialized(@Payload('initialized') payload: boolean) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: IMemberStateModel) => {
            draft.currentSession.initialized = payload;
        }));

        return payload;
    }

    @Action(ClearMemberSessionAction)
    clearMemberAction(ctx: StateContext<IMemberStateModel>, payload: ClearMemberSessionAction) {
        // this.clearMember(payload?.options?.keepContextProgress ?? false);
        this.removeExpiredContexts();
    }

    @DataAction()
    setBase64(@Payload('base64') payload: string) {

        let base64: string;
        let tenantId: string;
        let memberId: string;
        let legacyBase64: string;

        if (payload) {
            console.log('Before parse base64 => ', payload);
            base64 = Utilities.splitInTwo(payload, '#').firstPart;

            let parts: { firstPart: string, secondPart: string };
            console.log('Before get base64 parts => ', base64);
            parts = Utilities.splitInTwo(atob(base64), '|');

            tenantId = parts.firstPart.toUpperCase();
            console.log('Before get memberId from parts => ', parts.secondPart);
            memberId = Utilities.splitInTwo(parts.secondPart, '|').firstPart;

            console.log('Before generate the legacyBase64 => ', `${tenantId}|${memberId}|MemberActor`);
            legacyBase64 = btoa(`${tenantId}|${memberId}|MemberActor`);
        }

        this.ctx.setState(produce(this.ctx.getState(), (draft: IMemberStateModel) => {
            draft.currentSession.base64 = payload;
            draft.currentSession.tenantId = tenantId;
            draft.currentSession.memberId = memberId;
            draft.currentSession.legacyBase64 = legacyBase64;
        }));

        if (this.tenantIdSync !== this.previousTenantIdSync) {
            this.tenantChanged$.next(this.tenantIdSync);
        }

        return payload;
    }

    @DataAction()
    setMemberContext(@Payload('member') payload: MemberContext) {

        this.ctx.setState(produce(this.ctx.getState(), (draft: IMemberStateModel) => {
            draft.currentSession.memberContext = payload;
        }));

        this.blazorInteropService.sendToBlazor({type: 'angular-member-context-updated', message: undefined});
    }

    @DataAction()
    async setMMR(@Payload('mmr') payload: MMRRecord) {

        this.ctx.setState(produce(this.ctx.getState(), (draft: IMemberStateModel) => {
            draft.currentSession.mmr = payload;
        }));

        return payload;
    }

    //#region Task functions

    @DataAction()
    setOnCall(@Payload('onCall') payload: boolean) {
        if (this.snapshot.currentSession.memberContext) {
            this.ctx.setState(produce(this.ctx.getState(), (draft: IMemberStateModel) => {
                draft.currentSession.memberContext.metadata.onCall = payload;
            }));
        }
    }


    @DataAction()
    setTrackingCommands(payload: ITaskCommandWrapper[]) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: IMemberStateModel) => {
            draft.currentSession.trackingCommands = payload;
        }));

        return payload;
    }

    @DataAction()
    runCommands(@Payload('commands') payload: ICommand[]) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: IMemberStateModel) => {
            draft.currentSession.memberContext = { ...draft.currentSession.memberContext, commands: payload };
        }));

        return payload;
    }

    @DataAction()
    async updateTrackingCommand(@Payload('action') payload: ICommand) {
        this.ctx.setState(
            patch({
                currentSession:
                    patch({
                        trackingCommands: updateItem<ICommand>((command) => command.id === payload.id, payload)
                    })
            })

        );

        return payload;
    }

    @DataAction()
    async setSessionStatus(@Payload('status') payload: SessionStatus) {
        const currentState = this.ctx.getState();
        if (currentState.currentSession.memberContext) {
            this.ctx.setState(produce(this.ctx.getState(), (draft: IMemberStateModel) => {
                draft.currentSession.memberContext.metadata.sessionStatus = payload;
            }));
        }

        return payload;
    }

    //#region Member Session
    sync$: Subscription;
    visible: boolean;


    showMemberSessionSurvey: boolean;

    @Selector()
    static rawContextsSelector(state: IMemberStateModel) {
        return state.sessionsTracker.contexts || [];
    }

    @SelectorOptions({ injectContainerState: false })
    @Selector([MemberService.rawContextsSelector])
    static contextsSelector(contexts: TelephonyContext[]) {
        const result = Utilities.cloneHard(contexts);

        // Leonardo: Force tenantIds in context to be upper case
        result.forEach(contextItem => {
            if (contextItem.tenantId) {
                contextItem.tenantId = contextItem.tenantId.toUpperCase();
            }
        });
        return result;
    }

    private get contexts$(): Observable<TelephonyContext[]> {
        return this.store.select<TelephonyContext[]>(MemberService.contextsSelector);
    }

    private get contextsSync(): TelephonyContext[] {
        return this.store.selectSnapshot<TelephonyContext[]>(MemberService.contextsSelector);
    }

    get context$() {
        return this.contexts$.pipe(filter(_ => !!_ && _.length > 0), map(_ => {
            if (this.memberSync?.$memberHash) {
                const { contexts, contextIndex } = this.getCurrentTelephonyContext(this.memberSync.$memberHash, this.memberSync.id);
                return contexts[contextIndex];
            }
        }), filter(_ => !!_));
    }

    showDialog(visible: boolean) {
        this.visible = visible;
    }

    saveMemberSessionSurvey(survey?: ISurveyInfoViewModel) {

        const { contextIndex } = this.getCurrentTelephonyContext();
        if (contextIndex >= 0) {
            const merge = {
                ...survey,
                metaData: {
                    survey: {
                        rating: survey.rating
                    }
                }
            };

            return from(this.patchContext(merge, true));
        }
    }

    /*************************New Impelentation***************************************** */

    getCurrentTelephonyContext(hash?: string, memberId?: string) {
        let inContextMemberIndex: number;
        hash = hash ?? this.memberSync?.$memberHash;
        let contextIndex: number;
        let context: TelephonyContext;

        memberId = memberId ?? this.memberSync?.id;
        let memberContext: TelephonyMemberItem;

        if (hash) {
            const { context: contextTemp, contextIndex: contextIndexTemp } = MemberSessionService.getContextByMemberGroupHash(this.contextsSync, hash);
            contextIndex = contextIndexTemp;
            context = contextTemp;
        } else if (memberId) {
            const { context: contextTemp, contextIndex: contextIndexTemp } = MemberSessionService.getContextByMemberId(this.contextsSync, memberId);
            contextIndex = contextIndexTemp;
            context = contextTemp;


        }

        if (contextIndex >= 0 && memberId) {
            inContextMemberIndex = this.contextsSync[contextIndex].memberGroup.findIndex(memberItem => memberItem.id === memberId);
            if (inContextMemberIndex >= 0) {
                memberContext = this.contextsSync[contextIndex].memberGroup[inContextMemberIndex];
            }
        }

        return { contexts: this.contextsSync, context, contextIndex, memberContext, inContextMemberIndex };
    }

    async initializeSession() {
        // Wait for telephony params
        await firstValueFrom(this.telephonyParamsService.telephonyParamsAwaiter$.pipe(first()));

        await this.telephonyParamsService.manageTelephonyParams();
        this.removeExpiredContexts();

        await this.getOrCreateContext(this.memberSync.$memberHash);
    }

    private async getOrCreateContext(hash: string) {

        let context = MemberSessionService.getContextByMemberGroupHash(this.contextsSync, hash).context;

        if (!context) {
            context = await this.createMemberGroupContext();
        } else {

            context.$lastUpdate = new Date();
            await this.patchContext(context, false);
        }

        await this.getOrCreateContextMemberItem();

        return context;
    }

    /*************************Redux****************************** */

    @DataAction()
    private async createMemberGroupContext() {

        const date = new Date();
        let newContext = {
            $created: date,
            $lastUpdate: date,
        } as TelephonyContext;

        newContext.triggerMemberId = this.memberSync.id;
        newContext.memberGroupHash = this.memberSync.$memberHash;
        newContext.memberGroupHashSource = this.memberSync.$memberHashSource;
        newContext.tenantId = this.tenantIdSync;
        newContext.memberGroup = [];


        newContext = await this.applyQueryParamsToContext(newContext);
        newContext = await this.formatContext(newContext);

        try {
            this.ctx.setState(patch<IMemberStateModel>({ sessionsTracker: patch({ contexts: append([newContext]) }) }));
        } catch (ex) {
            console.error(`createMemberGroupContext setState error=> `, ex);
            throw ex;
        }

        return newContext;
    }

    @DataAction()
    async getOrCreateContextMemberItem() {
        const date = new Date();
        const { contexts, contextIndex, memberContext, inContextMemberIndex } = this.getCurrentTelephonyContext();


        let memberMatch: TelephonyMemberItem;
        if (!memberContext) {
            memberMatch = { id: this.memberSync.id, $created: date, $lastUpdate: date, status: 'active' } as TelephonyMemberItem;
            const sendResponse = await this.upsertTelephonyRecord(memberMatch);
            memberMatch.telephonyRecordId = sendResponse.id;
        } else {
            memberMatch = { ...Utilities.cloneHard(memberContext) };
            memberMatch.status = 'active';
            memberMatch.$lastUpdate = date;
        }


        try {
            this.ctx.setState(patch<IMemberStateModel>({
                sessionsTracker: patch({
                    contexts: updateItem(contextIndex, context => {
                        const newContext = { ...context } as TelephonyContext;
                        newContext.memberGroup = [...newContext.memberGroup];
                        if (inContextMemberIndex === -1) {
                            newContext.memberGroup.push(memberMatch);
                        } else {
                            newContext.memberGroup.splice(inContextMemberIndex, 1, memberMatch);
                        }

                        return newContext;
                    })
                })
            }));

        } catch (ex) {
            console.error(`getOrCreateContextMemberItem setState error=> `, ex);
            throw ex;
        }

        return this.contextsSync[contextIndex].memberGroup[inContextMemberIndex];
    }

    @DataAction()
    onLeaveMember() {
        const { contextIndex, inContextMemberIndex } = this.getCurrentTelephonyContext();

        if (inContextMemberIndex >= 0) {
            this.ctx.setState(patch<IMemberStateModel>({
                sessionsTracker: patch({
                    contexts: updateItem<TelephonyContext>(contextIndex, context => {

                        const newContext = Utilities.cloneHard(context) as TelephonyContext;
                        newContext.memberGroup[inContextMemberIndex].$lastUpdate = new Date();
                        newContext.$lastUpdate = new Date();

                        return newContext;
                    })
                })
            }));

        }
    }

    // Not working
    @DataAction()
    async completeMemberContext() {
        const { contextIndex, memberContext, inContextMemberIndex } = this.getCurrentTelephonyContext();

        if (inContextMemberIndex === -1) {
            return;
        }

        const request = Utilities.cloneHard(memberContext);
        request.status = 'finished';

        let result: TelephonyContextResponse;
        try {
            result = await this.upsertTelephonyRecord(request);

            this.ctx.setState(patch<IMemberStateModel>({
                sessionsTracker: patch({
                    contexts: updateItem<TelephonyContext>(contextIndex, context => {

                        const newContext = Utilities.cloneHard<TelephonyContext>(context);
                        newContext.memberGroup[inContextMemberIndex].status = 'finished';
                        newContext.memberGroup[inContextMemberIndex].$lastUpdate = new Date();
                        newContext.$lastUpdate = new Date();

                        return newContext;
                    })
                })
            }));


        } catch (ex) {
            this.alertService.showMessage(`Error completing session`);
        }

        return result;
    }

    @DataAction()
    async patchContext(@Payload('context') itemPatch: TelephonyContext, saveRecord?: boolean) {

        const { contexts, contextIndex, memberContext, inContextMemberIndex } = this.getCurrentTelephonyContext();
        let sendResponse: TelephonyContextResponse;
        let merge: TelephonyContext;

        if (contextIndex >= 0) {

            try {

                merge = await this.mergeContext(contexts, contextIndex, itemPatch);

                sendResponse = saveRecord ? await this.upsertTelephonyRecord(memberContext, merge) : undefined;

                if (inContextMemberIndex >= 0) {
                    merge.memberGroup[inContextMemberIndex].telephonyRecordId = (sendResponse && sendResponse.id) || merge.memberGroup[inContextMemberIndex].telephonyRecordId;
                    merge.memberGroup[inContextMemberIndex].$lastUpdate = new Date();
                }
                // Update context item
                this.ctx.setState(patch<IMemberStateModel>({
                    sessionsTracker: patch({
                        contexts: updateItem(contextIndex, context => {
                            return merge;
                        })
                    })
                }));

            } catch (ex) {
                this.alertService.showMessage(`Error patching context`);
                throw ex;
            }
        }
    }

    @DataAction()
    private removeContext(context: TelephonyContext) {
        const triggerMemberId = context.triggerMemberId;
        const memberGroupHash = context.memberGroupHash;
        try {
            this.ctx.setState(patch<IMemberStateModel>({ sessionsTracker: patch({ contexts: removeItem<TelephonyContext>(contextItem => contextItem.memberGroupHash === memberGroupHash && contextItem.triggerMemberId === triggerMemberId) }) }));
        } catch (ex) {
            console.error(`removeContextBySessionIdentifier error => `, ex);
        }

    }

    @DataAction()
    cleanDisposition() {
        const { contextIndex, inContextMemberIndex } = this.getCurrentTelephonyContext();

        if (inContextMemberIndex >= 0) {
            this.ctx.setState(patch<IMemberStateModel>({
                sessionsTracker: patch({
                    contexts: updateItem<TelephonyContext>(contextIndex, context => {
                        const newContext = { ...context } as TelephonyContext;

                        newContext.dispositionIdentifier = null;
                        newContext.dispositionId = null;
                        // newContext.deleted = null;
                        newContext.$lastUpdate = new Date();

                        return newContext;
                    })
                })
            }));

        }
    }

    /*****************************New implementation utils********************************** */

    private async mergeContext(contexts: TelephonyContext[], contextIndex: number, itemPatch: TelephonyContext): Promise<TelephonyContext> {
        let result = {
            ...Utilities.cloneHard(contexts[contextIndex]), ...{
                tenantId: itemPatch.tenantId || contexts[contextIndex].tenantId,
                direction: itemPatch.direction || contexts[contextIndex].direction || 'MANUAL',
                directionId: itemPatch.directionId || contexts[contextIndex].directionId,

                contactId: itemPatch.contactId || contexts[contextIndex].contactId,

                notes: itemPatch.notes || contexts[contextIndex].notes,

                metaData: {
                    ...contexts[contextIndex].metaData,
                    ...itemPatch.metaData
                },

                origin: itemPatch.origin || contexts[contextIndex].origin,
                originId: itemPatch.originId || contexts[contextIndex].originId,

                reason: itemPatch.reason || contexts[contextIndex].reason,
                reasonId: itemPatch.reasonId || contexts[contextIndex].reasonId,

                skillname: itemPatch.skillname || contexts[contextIndex].skillname,
                dispositionId: itemPatch.dispositionId || contexts[contextIndex].dispositionId,
                dispositionIdentifier: itemPatch.dispositionIdentifier || contexts[contextIndex].dispositionIdentifier,
                $lastUpdate: new Date(),
            }
        } as TelephonyContext;

        return await this.formatContext(result);
    }

    private async formatContext(newContext: TelephonyContext): Promise<TelephonyContext> {

        let directionMatch: LabelValueLower;
        if (newContext.directionId) {
            directionMatch = Directions.find(directionItem => directionItem.value === newContext.directionId);
        }
        if (!directionMatch && newContext.direction) {
            directionMatch = Directions.find(directionItem => directionItem.label === newContext.direction);
        }

        if (directionMatch) {
            newContext.direction = directionMatch.label;
            newContext.directionId = directionMatch.value;
        }

        if (!!newContext.direction) {
            if (newContext.direction === 'INBOUND') {
                newContext.reason = undefined;
                newContext.reasonId = undefined;
            } else if (newContext.direction !== 'INBOUND') {
                newContext.origin = undefined;
                newContext.originId = undefined;
            }
        }


        return newContext;
    }

    public async getSurveyRating(tenantId, memberId) : Promise<any> {
        return await this.memberEndpoint.getMemberSurveyRating(tenantId, memberId);
    }

    private async upsertTelephonyRecord(memberContext?: TelephonyMemberItem, telephonyContext?: TelephonyContext): Promise<TelephonyContextResponse> {
        if (!this.memberSync) {
            console.log(`No member selected yet`);
            this.appInsightsService.logException(new Error(`No member selected yet`), 'upsertTelephonyRecord', { telephonyContext, memberContext, contexts: this.contextsSync, params: this.telephonyParamsService.formattedQueryParamsSync });
            return;
        }

        if (!memberContext || !telephonyContext) {
            const { memberContext: memberItemConst, contexts, contextIndex } = this.getCurrentTelephonyContext();
            if (memberItemConst) {
                memberContext = memberItemConst;
            }

            if (!telephonyContext) {
                telephonyContext = contexts[contextIndex];
            }

            if (!memberContext || !telephonyContext) {
                this.appInsightsService.logException(new Error(`After tried to get member context`), 'upsertTelephonyRecord', { telephonyContext, memberContext, contexts: this.contextsSync, params: this.telephonyParamsService.formattedQueryParamsSync });
            }
        }

        const request = { ...telephonyContext, ...memberContext } as TelephonyContextRequest;
        request.id = memberContext?.telephonyRecordId;
        request.memberId = memberContext?.id || this.memberSync?.id;


        const result = await this.memberEndpoint.recordCall(request).pipe(first()).toPromise();
        return result;
    }

    private removeExpiredContexts() {

        if (this.contextsSync) {

            const { contexts, context: currentContext, contextIndex } = this.getCurrentTelephonyContext();

            const expiredContexts = this.contextsSync.filter(item => {

                let expired = false;

                // Same member was access with access with different parameters. Remove it if is the only member in the context
                if (currentContext && item.memberGroupHash == currentContext.memberGroupHash && item.paramsHash != this.telephonyParamsService.formattedQueryParamsSync?.paramsHash

                    // is the only one
                    && (contexts.length === 1 && contexts[0].memberGroup?.length === 1)

                ) {
                    expired = true;
                } else if (item.memberGroup.every(memberItem => memberItem.status === 'finished')) {
                    expired = true;
                } else
                    if (!currentContext || item.memberGroupHash != currentContext.memberGroupHash || item.paramsHash != currentContext.paramsHash) {

                        const now = moment(new Date());
                        const lastUpdate = moment(item.$lastUpdate);
                        const minDiff = moment.duration(now.diff(lastUpdate)).asMinutes();


                        expired = minDiff > this.configurationService.memberSessionExpirationMinutes;
                    }

                return expired;
            });

            for (const expiredContext of expiredContexts) {
                this.removeContext(expiredContext);
            }
        }
    }

    //#endregion

    @DataAction()
    async handleNavigation(@Payload('subscriber') subscriber: INavigationSubscriber, @Payload('current route') route?: ActivatedRoute) {

        const { memberContext, contexts, contextIndex } = this.getCurrentTelephonyContext();
        if (this.memberIdSync && memberContext) {
            this.ctx.setState(patch<IMemberStateModel>({
                sessionsTracker: patch({
                    contexts: updateItem(contextIndex, (context: TelephonyContext) => {
                        context = { ...context, $lastUpdate: new Date() };
                        return context;
                    })
                })
            }));
        }
    }


    private async applyQueryParamsToContext(context: TelephonyContext) {
        const queryParams = await this.telephonyParamsService.formattedQueryParams$.pipe(filter(_ => !!_), first()).toPromise();

        const direction = queryParams.direction?.toUpperCase();

        const newContext = {
            ...context,

            skillname: queryParams.skillName || context.skillname,
            direction: direction || context.direction,
            contactId: queryParams.contactId || context.contactId,
            metaData: queryParams.meta || queryParams.metadata || queryParams.metaData || context.metaData,
            paramsHash: queryParams.paramsHash,
            paramsDate: queryParams.paramsDate,
        } as TelephonyContext;

        return newContext;
    }

}
